import React from "react";
import { useTranslation } from "react-i18next";
import { FORM_NAME } from "src/constant/common";

const FieldValue = ({ field, mcsSigmaParamObj }) => {
  const { key, label, unit, isArray, isMatrix } = field;
  const value = mcsSigmaParamObj?.[key]?.value;

  const formatValue = (val) => {
    if (isMatrix) return val?.map((row) => `[${row.join(", ")}]`).join(", ");
    if (isArray) return `[${Array.isArray(val) ? val.join(", ") : val}]`;
    return val;
  };

  return (
    <div className="ms-2 mb-3">
      <span>{label}</span>
      {unit ? <span> ({unit}) = </span> : <span> = </span>}
      <span>{formatValue(value)}</span>
    </div>
  );
};

const Section = ({ title, fields, mcsSigmaParamObj }) => (
  <div className="fw-bold mb-3">
    <h5 className="fw-bold mb-3">{title}</h5>
    {fields.map((field) => (
      <FieldValue field={field} mcsSigmaParamObj={mcsSigmaParamObj} key={field.key} />
    ))}
  </div>
);

const MCSSigmaParam = ({ simulationData }) => {
  const { t } = useTranslation("gnc-simulator-6dof");
  const mcsSigmaParamObj = simulationData?.input_data?.MCS;

  const sections = [
    {
      title: t(`new.MCS.initial_condition_sigma_title`),
      fields: [
        { key: "sigma_pos0", label: t(`new.MCS.${FORM_NAME.MCS.POSITION}`), unit: "m", isArray: true },
        { key: "sigma_vel0", label: t(`new.MCS.${FORM_NAME.MCS.VELOCITY}`), unit: "m/s", isArray: true },
        { key: "sigma_euler0", label: t(`new.MCS.${FORM_NAME.MCS.ATTITUDE_ANGLE}`), unit: "deg", isArray: true },
        { key: "sigma_omega0", label: t(`new.MCS.${FORM_NAME.MCS.ANGULAR_VELOCITY}`), unit: "deg/s", isArray: true },
      ],
    },
    {
      title: t(`new.MCS.vehicle_sigma_title`),
      fields: [
        { key: "sigma_m_dry", label: t(`new.MCS.${FORM_NAME.MCS.DRY_MASS}`) },
        { key: "sigma_CG_s", label: t(`new.MCS.${FORM_NAME.MCS.CENTER_OF_GRAVITY_TOTAL_MASS}`), unit: "m", isArray: true },
        { key: "sigma_CG_e", label: t(`new.MCS.${FORM_NAME.MCS.CENTER_OF_GRAVITY_DRY_MASS}`), unit: "m", isArray: true },
        { key: "sigma_CG_tank", label: t(`new.MCS.${FORM_NAME.MCS.CENTER_OF_GRAVITY_TANK}`), unit: "m", isArray: true },
        { key: "sigma_pos_fuel_s", label: t(`new.MCS.${FORM_NAME.MCS.FUEL_POSITION_TOTAL_MASS}`), unit: "m", isArray: true },
        { key: "sigma_pos_fuel_e", label: t(`new.MCS.${FORM_NAME.MCS.FUEL_POSITION_DRY_MASS}`), unit: "m", isArray: true },
        { key: "sigma_m_propellant", label: t(`new.MCS.${FORM_NAME.MCS.PROPELLANT_MASS}`) },
        {
          key: "sigma_I_s",
          label: t(`new.MCS.${FORM_NAME.MCS.MOMENT_OF_INERTIA_TOTAL_MASS}`),
          unit: <>kgm&sup2;</>,
          isMatrix: true,
        },
        {
          key: "sigma_I_e",
          label: t(`new.MCS.${FORM_NAME.MCS.MOMENT_OF_INERTIA_DRY_MASS}`),
          unit: <>kgm&sup2;</>,
          isMatrix: true,
        },
      ],
    },
    {
      title: t(`new.MCS.aerodynamic_sigma_title`),
      fields: [
        { key: "sigma_CA_data", label: t(`new.MCS.${FORM_NAME.MCS.CA}`) },
        { key: "sigma_CY_data", label: t(`new.MCS.${FORM_NAME.MCS.CY}`) },
        { key: "sigma_CN_data", label: t(`new.MCS.${FORM_NAME.MCS.CN}`) },
        { key: "sigma_Cl_data", label: t(`new.MCS.${FORM_NAME.MCS.Cl}`) },
        { key: "sigma_Cm_data", label: t(`new.MCS.${FORM_NAME.MCS.Cm}`) },
        { key: "sigma_Cn_data", label: t(`new.MCS.${FORM_NAME.MCS.Cn}`) },
      ],
    },
    {
      title: t(`new.MCS.engine_sigma_title`),
      fields: [
        { key: "sigma_Isp", label: t(`new.MCS.${FORM_NAME.MCS.VACUUM_ISP}`) },
        { key: "sigma_thrust_tau", label: t(`new.MCS.${FORM_NAME.MCS.THRUST_TIME_CONSTANT}`) },
        { key: "sigma_L_engine_pitch", label: t(`new.MCS.${FORM_NAME.MCS.ENGINE_PITCH_LENGTH}`) },
        { key: "sigma_thrust_max", label: t(`new.MCS.${FORM_NAME.MCS.MAX_THRUST_OF_ENGINE}`) },
        { key: "sigma_thrust_DT", label: t(`new.MCS.${FORM_NAME.MCS.THRUST_DELAY_TIME}`) },
        { key: "sigma_TVC1_pos", label: t(`new.MCS.${FORM_NAME.MCS.TVC1_POSITION}`), unit: "m", isArray: true },
        { key: "sigma_TVC2_pos", label: t(`new.MCS.${FORM_NAME.MCS.TVC2_POSITION}`), unit: "m", isArray: true },
      ],
    },
    {
      title: t(`new.MCS.tvc_sigma_title`),
      fields: [
        { key: "sigma_omega_c", label: t(`new.MCS.${FORM_NAME.MCS.TVC_ACTUATOR_NATURAL_FREQUENCY}`) },
        { key: "sigma_omega_T", label: t(`new.MCS.${FORM_NAME.MCS.TVC_ACTUATOR_FREQUENCY}`) },
        { key: "sigma_TVC_min", label: t(`new.MCS.${FORM_NAME.MCS.MINIMUM_TVC_DEFLECTION}`), unit: "rad" },
        { key: "sigma_zeta", label: t(`new.MCS.${FORM_NAME.MCS.DAMPING_RATIO}`) },
        { key: "sigma_TVC_max", label: t(`new.MCS.${FORM_NAME.MCS.MAXIMUM_TVC_DEFLECTION}`), unit: "rad" },
        { key: "sigma_TVC_rateLim", label: t(`new.MCS.${FORM_NAME.MCS.MAXIMUM_TVC_DEFLECTION_LIMIT}`), unit: "rad/s" },
      ],
    },
    {
      title: t(`new.MCS.rcs_sigma_title`),
      fields: [
        { key: "sigma_Isp_RCS", label: t(`new.MCS.${FORM_NAME.MCS.RCS_SPECIFIC_IMPULSE}`) },
        { key: "sigma_Fc", label: t(`new.MCS.${FORM_NAME.MCS.THRUST_OF_RCS_THRUSTER}`) },
        { key: "sigma_DT_RCS", label: t(`new.MCS.${FORM_NAME.MCS.DEAD_TIME_OF_RCS_SYSTEM}`) },
        { key: "sigma_m_RCS", label: t(`new.MCS.${FORM_NAME.MCS.FUEL_MASS_RCS}`) },
        { key: "sigma_tau_RCS", label: t(`new.MCS.${FORM_NAME.MCS.TIME_CONSTANT_OF_RCS_SYSTEM}`) },
      ],
    },
    {
      title: t(`new.MCS.wind_and_disturbance_sigma_title`),
      fields: [
        { key: "sigma_wind_speed", label: t(`new.MCS.${FORM_NAME.MCS.WIND_SPEED_AT_INITIAL_ALTITUDE}`), unit: "m/s" },
        { key: "sigma_wind_direction", label: t(`new.MCS.${FORM_NAME.MCS.WIND_DIRECTION_AT_INITIAL_ALTITUDE}`), unit: "deg" },
      ],
    },
    {
      title: t(`new.MCS.mrn_01_sigma_title`),
      fields: [
        { key: "sigma_max_acc", label: t(`new.MCS.${FORM_NAME.MCS.MAXIMUM_ACCELERATION}`) },
        {
          key: "sigma_RW_acc",
          label: t(`new.MCS.${FORM_NAME.MCS.VELOCITY_RANDOM_WALK}`),
        },
        {
          key: "sigma_TB_acc",
          label: t(`new.MCS.${FORM_NAME.MCS.TEMPERATURE_BIAS_OF_ACCELEROMETER}`),
        },
        {
          key: "sigma_LSB_gyro",
          label: t(`new.MCS.${FORM_NAME.MCS.GYROSCOPE_LSB_RESOLUTION}`),
        },
        {
          key: "sigma_BU_gyro",
          label: t(`new.MCS.${FORM_NAME.MCS.GYRO_BIAS}`),
        },
        {
          key: "sigma_skew",
          label: t(`new.MCS.${FORM_NAME.MCS.SKEW_OF_SENSOR_AXES}`),
          isArray: true,
        },
        {
          key: "sigma_LSB_acc",
          label: t(`new.MCS.${FORM_NAME.MCS.ACCELEROMETER_LSB_RESOLUTION}`),
        },
        {
          key: "sigma_BU_acc",
          label: t(`new.MCS.${FORM_NAME.MCS.ACCELEROMETER_BIAS}`),
        },
        {
          key: "sigma_max_gyro",
          label: t(`new.MCS.${FORM_NAME.MCS.MAX_GYRO}`),
        },
        {
          key: "sigma_RW_gyro",
          label: t(`new.MCS.${FORM_NAME.MCS.ANGLE_RANDOM_WALK}`),
        },
        {
          key: "sigma_TB_gyro",
          label: t(`new.MCS.${FORM_NAME.MCS.TEMPERATURE_BIAS_OF_GYRO}`),
        },
      ],
    },
    {
      title: t(`new.MCS.gnss_sigma_title`),
      fields: [
        { key: "sigma_pos_err1", label: t(`new.MCS.${FORM_NAME.MCS.HORIZONTAL_POSITION_ACCURACY}`) },
        { key: "sigma_pos_err2", label: t(`new.MCS.${FORM_NAME.MCS.VERTICAL_POSITION_ACCURACY}`) },
        { key: "sigma_vel_err", label: t(`new.MCS.${FORM_NAME.MCS.VELOCITY_ACCURACY}`) },
      ],
    },
  ];

  return (
    <div className="m-3">
      {sections.map(({ title, fields }) => (
        <Section title={title} fields={fields} mcsSigmaParamObj={mcsSigmaParamObj} key={title} />
      ))}
    </div>
  );
};

export default MCSSigmaParam;
